import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import usePermissions from "../../../Components/Hooks/usePermissions";

/* 🔹 Thunks (create later if not exist) */
import {
  fetchAllSubscriptionPlans,
  fetchSubscriptionPlanTableColumns,
  deleteSubscriptionPlan,
  activateDeactivateSubscriptionPlan,
  updateUserPreference,
} from "../../../slices/thunks";

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit Plan",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete Plan",
  },
};

const SubscriptionPlan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  /* 🔐 Permissions
     moduleId & pageId → adjust based on your system */
  const permission = (() => {
    return usePermissions(2, 10); // Example: Administration → Subscription Plans
  })();

  const {
    plans,
    loading,
    tableColumnNames,
  } = useSelector((state) => ({
    plans: state.SubscriptionPlan.plans,
    tableColumnNames: state.SubscriptionPlan.tableColumnNames,
    loading: state.SubscriptionPlan.loading,
  }));

  /* ---------------- Handlers ---------------- */

  const onAddPlanClick = () => {
    navigate("/payment/subscription-plan/add");
  };

  const onEditClickHandler = (plan) => {
    navigate(`/payment/subscription-plan/edit/${plan.ID}`);
  };

  const onActiveOrDeactiveChange = (plan) => {
    dispatch(activateDeactivateSubscriptionPlan({ ID: plan.ID }));
  };

  const confirmDelete = () => {
    dispatch(deleteSubscriptionPlan({ IDs: selectedIds }))
      .then(() => setSelectedIds([]));
    setDeleteModal(false);
  };

  const saveUserPreferences = async () => {
    const payload = {
      ColumnIDs: tableColumnNames.reduce((acc, col) => {
        acc[col.ID] = selectedColumns.includes(col.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    await dispatch(updateUserPreference(payload));
    await dispatch(
      fetchSubscriptionPlanTableColumns({ module: "SUBSCRIPTION_PLAN" })
    );
  };

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    dispatch(
      fetchSubscriptionPlanTableColumns({
        module: "SUBSCRIPTION_PLAN",
      })
    );
    dispatch(fetchAllSubscriptionPlans());
  }, [dispatch]);

  /* ---------------- UI ---------------- */

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("SubscriptionPlans")} />
          </Col>
        </Row>

        <Card>
          <CardHeader>
            <Row className="g-4">
              <Col className="col-sm-auto">
                <div>
                  <Button
                    className="btn btn-soft-danger ms-2 me-2"
                    onClick={() => setDeleteModal(true)}
                    disabled={selectedIds.length === 0}
                  >
                    <i className="ri-delete-bin-2-line"></i>
                  </Button>

                  {permission.CanAdd && (
                    <Button
                      color="secondary"
                      className="add-btn me-1"
                      onClick={onAddPlanClick}
                      disabled={loading}
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      {t("AddNew")}
                    </Button>
                  )}
                </div>
              </Col>

              <Col className="col-sm">
                <div className="d-flex justify-content-sm-end">
                  <Button
                    className="btn btn-soft"
                    onClick={() => setModalOpen(true)}
                    disabled={loading}
                  >
                    <i className="ri-filter-3-line"></i>
                  </Button>

                  <div className="search-box ms-2 me-2">
                    <Input
                      type="text"
                      className="form-control search"
                      placeholder={t("Search")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </div>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner style={{ height: "3rem", width: "3rem" }} />
              </div>
            ) : (
              <CommonDataTable
                data={plans}
                tableColumnNames={tableColumnNames}
                searchTerm={searchTerm}
                onEditClickHandler={onEditClickHandler}
                onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                setDeleteModal={setDeleteModal}
                setSelectedIds={setSelectedIds}
                actionConfig={actionConfig}
                permission={permission}
              />
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Column selection */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </div>
  );
}

export default SubscriptionPlan;